import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  X,
  Search,
  Plus,
  AlertCircle,
  CheckCircle,
  Percent,
  Calendar,
  FileText
} from 'lucide-react';
import { apiService } from '../../services/api';
import { EmployeeWithAssignments, AssignEmployeeRequest } from '../../types/employee';

interface AssignmentModalProps {
  employee: EmployeeWithAssignments;
  onClose: () => void;
  onSuccess: () => void;
}

export const AssignmentModal: React.FC<AssignmentModalProps> = ({
  employee,
  onClose,
  onSuccess
}) => {
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [commissionPercentage, setCommissionPercentage] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [isActive, setIsActive] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [validationResult, setValidationResult] = useState<any>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch available accounts for this employee
  const {
    data: accountsData,
    isLoading: accountsLoading
  } = useQuery({
    queryKey: ['available-accounts', employee.id, searchTerm],
    queryFn: () => apiService.getAvailableAccountsForEmployee(employee.id, searchTerm),
    staleTime: 5 * 60 * 1000,
  });

  // Validate commission allocation
  const validateMutation = useMutation({
    mutationFn: (data: { account_id: number; commission_percentage: number }) =>
      apiService.validateCommissionAllocation(data),
    onSuccess: (response) => {
      setValidationResult(response.data);
    },
  });

  // Assign employee to account
  const assignMutation = useMutation({
    mutationFn: (data: AssignEmployeeRequest) => apiService.assignEmployeeToAccount(data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (error: any) => {
      const errorData = error.response?.data;
      if (errorData?.errors) {
        setErrors(errorData.errors);
      }
    },
  });

  // Validate commission percentage when it changes
  useEffect(() => {
    if (selectedAccountId && commissionPercentage && parseFloat(commissionPercentage) > 0) {
      validateMutation.mutate({
        account_id: selectedAccountId,
        commission_percentage: parseFloat(commissionPercentage),
      });
    } else {
      setValidationResult(null);
    }
  }, [selectedAccountId, commissionPercentage, validateMutation]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!selectedAccountId) {
      setErrors({ account_id: 'Please select an account' });
      return;
    }

    if (!commissionPercentage || parseFloat(commissionPercentage) <= 0) {
      setErrors({ commission_percentage: 'Please enter a valid commission percentage' });
      return;
    }

    if (parseFloat(commissionPercentage) > 100) {
      setErrors({ commission_percentage: 'Commission percentage cannot exceed 100%' });
      return;
    }

    const assignmentData: AssignEmployeeRequest = {
      user_id: employee.id,
      account_id: selectedAccountId,
      commission_percentage: parseFloat(commissionPercentage),
      is_active: isActive,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      notes: notes || undefined,
    };

    assignMutation.mutate(assignmentData);
  };

  const availableAccounts = accountsData?.data || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Assign Account to {employee.name}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Set up a new account assignment with commission percentage
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Form */}
          <div className="p-6 space-y-6">
            {/* Account Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Account *
              </label>

              {/* Search */}
              <div className="relative mb-3">
                <Search className="h-4 w-4 text-gray-400 absolute left-3 top-3" />
                <input
                  type="text"
                  placeholder="Search accounts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Account List */}
              <div className="border border-gray-300 rounded-lg max-h-48 overflow-y-auto">
                {accountsLoading ? (
                  <div className="p-4 text-center text-gray-500">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                    <span className="mt-2 block">Loading accounts...</span>
                  </div>
                ) : availableAccounts.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    <span>No available accounts found</span>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {availableAccounts.map((account) => (
                      <label
                        key={account.id}
                        className={`flex items-center p-3 cursor-pointer hover:bg-gray-50 ${
                          selectedAccountId === account.id ? 'bg-blue-50' : ''
                        }`}
                      >
                        <input
                          type="radio"
                          name="account"
                          value={account.id}
                          checked={selectedAccountId === account.id}
                          onChange={() => setSelectedAccountId(account.id)}
                          className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <div className="ml-3 flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {account.account_name}
                          </div>
                          {account.client && (
                            <div className="text-sm text-gray-500">
                              Client: {account.client.client_name}
                            </div>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {errors.account_id && (
                <div className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.account_id}
                </div>
              )}
            </div>

            {/* Commission Percentage */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Commission Percentage *
              </label>
              <div className="relative">
                <Percent className="h-4 w-4 text-gray-400 absolute left-3 top-3" />
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  placeholder="25.00"
                  value={commissionPercentage}
                  onChange={(e) => setCommissionPercentage(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Validation Result */}
              {validationResult && (
                <div className={`mt-2 p-3 rounded-lg ${
                  validationResult.is_valid ? 'bg-green-50' : 'bg-yellow-50'
                }`}>
                  <div className={`flex items-center text-sm ${
                    validationResult.is_valid ? 'text-green-800' : 'text-yellow-800'
                  }`}>
                    {validationResult.is_valid ? (
                      <CheckCircle className="h-4 w-4 mr-2" />
                    ) : (
                      <AlertCircle className="h-4 w-4 mr-2" />
                    )}
                    <span className="font-medium">
                      {validationResult.is_valid ? 'Valid allocation' : 'Allocation warning'}
                    </span>
                  </div>
                  <div className="mt-1 text-sm space-y-1">
                    <div className="flex justify-between">
                      <span>Existing allocation:</span>
                      <span>{validationResult.existing_percentage.toFixed(2)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>New allocation:</span>
                      <span>{validationResult.new_percentage.toFixed(2)}%</span>
                    </div>
                    <div className="flex justify-between font-medium">
                      <span>Total allocation:</span>
                      <span>{validationResult.total_percentage.toFixed(2)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>MCH share:</span>
                      <span>{validationResult.mch_percentage.toFixed(2)}%</span>
                    </div>
                    {validationResult.over_allocation > 0 && (
                      <div className="flex justify-between text-yellow-600">
                        <span>Over-allocation:</span>
                        <span>{validationResult.over_allocation.toFixed(2)}%</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {errors.commission_percentage && (
                <div className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {errors.commission_percentage}
                </div>
              )}
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <div className="relative">
                  <Calendar className="h-4 w-4 text-gray-400 absolute left-3 top-3" />
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <div className="relative">
                  <Calendar className="h-4 w-4 text-gray-400 absolute left-3 top-3" />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                    className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <div className="relative">
                <FileText className="h-4 w-4 text-gray-400 absolute left-3 top-3" />
                <textarea
                  placeholder="Optional notes about this assignment..."
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Active Status */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                Assignment is active
              </label>
            </div>

            {/* Global Error */}
            {assignMutation.isError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                  <span className="text-red-800">
                    {assignMutation.error?.message || 'Failed to assign employee to account'}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={assignMutation.isPending || !selectedAccountId || !commissionPercentage}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {assignMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Assigning...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Assign Account
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};